import { useCallback } from 'react';
import { endpoints, type CalendarEvent, type FeedItem, type OutlookCalendar, type WeatherWarning } from '../services/api';
import { fetchForecast, type Forecast } from '../services/weather';
import { useSession } from '../store/session';
import { useSettings, type CalendarSource } from '../store/settings';
import { usePolling, type PollState } from './usePolling';

export function useWeather(): PollState<Forecast> {
  const { enabled, location, unit, windUnit } = useSettings((s) => s.weather);
  const key = enabled && location ? `${location.latitude},${location.longitude},${unit},${windUnit}` : null;
  const load = useCallback(
    (signal: AbortSignal) => fetchForecast(location!, { unit, windUnit }, signal),
    [location, unit, windUnit],
  );
  return usePolling(key, load, 30 * 60_000);
}

/** Official warnings (MeteoAlarm) for the weather location's area; null when unsupported. */
export function useWarnings(): PollState<{ local: WeatherWarning[]; elsewhere: number }> {
  const { enabled, location, showWarnings } = useSettings((s) => s.weather);
  const country = location?.countryCode;
  const place = location ? [location.name, location.region].filter(Boolean).join(',') : '';
  const key = enabled && showWarnings && country ? `${country}|${place}` : null;
  const load = useCallback(() => endpoints.warnings(country!, place), [country, place]);
  return usePolling(key, load, 15 * 60_000);
}

export interface NewsItem extends FeedItem {
  source: string;
}

export function useNews(): PollState<NewsItem[]> {
  const { enabled, sources } = useSettings((s) => s.news);
  const active = sources.filter((s) => s.enabled);
  const key = enabled && active.length ? active.map((s) => s.url).join('|') : null;
  const load = useCallback(async () => {
    const results = await Promise.allSettled(active.map((s) => endpoints.rss(s.url).then((f) => ({ s, f }))));
    const items = results.flatMap((r) =>
      r.status === 'fulfilled' ? r.value.f.items.slice(0, 12).map((i) => ({ ...i, source: r.value.s.name })) : [],
    );
    if (!items.length && results.some((r) => r.status === 'rejected')) throw new Error('News feeds unavailable');
    // Interleave sources by recency.
    return items.sort((a, b) => (b.published ?? '').localeCompare(a.published ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return usePolling(key, load, 15 * 60_000);
}

export interface AgendaEvent extends Omit<CalendarEvent, 'calendar'> {
  calendar: Pick<CalendarSource, 'name' | 'color'>;
}

/** Outlook calendars on the connected account (for the settings list); null if not connected. */
export function useOutlookCalendars(): PollState<OutlookCalendar[]> {
  const microsoft = useSession((s) => s.microsoft?.connected ?? false);
  const load = useCallback(() => endpoints.outlookCalendars().then((r) => r.calendars), []);
  return usePolling(microsoft ? 'outlook-calendars' : null, load, 30 * 60_000);
}

/** True when the calendar widget has something to show: iCal feeds or a connected Outlook. */
export function useHasCalendarSource(): boolean {
  const { sources, outlook } = useSettings((s) => s.calendar);
  const microsoft = useSession((s) => s.microsoft?.connected ?? false);
  return sources.some((s) => s.enabled) || (outlook && microsoft);
}

/** Events from all enabled calendars for ange (start = local YYYY-MM-DD). */
export function useCalendar(range: { start: string; days: number }): PollState<AgendaEvent[]> {
  const { enabled, sources, outlook, outlookHidden } = useSettings((s) => s.calendar);
  const microsoft = useSession((s) => s.microsoft?.connected ?? false);
  const useOutlook = outlook && microsoft;
  const active = sources.filter((s) => s.enabled);
  const start = range.start;
  const days = range.days;
  const key =
    enabled && (active.length || useOutlook)
      ? `${start}|${days}|${useOutlook ? `outlook:${outlookHidden.join(',')}|` : ''}${active.map((s) => s.url + s.color).join('|')}`
      : null;
  const load = useCallback(async () => {
    const requests: Array<Promise<{ calendar: Pick<CalendarSource, 'name' | 'color'> | null; events: CalendarEvent[] }>> = [
      ...active.map((s) =>
        endpoints.ics(s.url, days, start).then((r) => ({ calendar: { name: s.name, color: s.color }, events: r.events })),
      ),
      // Outlook events carry their own calendar's name and colour.
      ...(useOutlook ? [endpoints.outlookCalendar(days, outlookHidden, start).then((r) => ({ calendar: null, events: r.events }))] : []),
    ];
    const results = await Promise.allSettled(requests);
    const events: AgendaEvent[] = results.flatMap((res) =>
      res.status === 'fulfilled'
        ? res.value.events.map((e) => ({
            ...e,
            calendar: res.value.calendar ?? { name: e.calendar?.name ?? 'Outlook', color: e.calendar?.color ?? '#3b82f6' },
          }))
        : [],
    );
    if (!events.length && results.some((r) => r.status === 'rejected')) throw new Error('Calendars unavailable');
    return events.sort((a, b) => a.start.localeCompare(b.start));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return usePolling(key, load, 10 * 60_000);
}
